/**
 * The modules row — the menu's top level, across the top of the screen, as a
 * WAI-ARIA menubar. A folder (or anything carrying entries, a website) is a
 * module that opens its mega panel; a top-level SCREEN is a plain module
 * button that opens it.
 *
 * Keyboard: Left/Right move between modules (and, while a panel is open,
 * carry the panel along), Home/End jump, Down / Enter / Space open the
 * focused module's panel on its first entry, Up on its last, Esc closes it.
 * One module is in the tab order at a time (roving tabindex).
 */
import { ChevronDown } from 'lucide-react'
import { useState, type KeyboardEvent, type RefObject } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../../nav/navIcons'
import type { NavTreeNode } from '../../nav/tree'
import type { ShellNavModel } from '../../nav/useShellNav'

/** A module opens a panel when it is a folder (even an empty one — the panel
 *  says so) or when it carries entries; otherwise it is a screen. */
export function hasPanel(node: NavTreeNode): boolean {
  return node.isFolder || node.children.length > 0
}

export type PanelFocus = 'first' | 'last' | { folderId: string }

export interface ModulesBarProps {
  model: ShellNavModel
  barRef: RefObject<HTMLDivElement | null>
  openId: string | null
  /** Open a module's panel (null closes), optionally moving focus into it. */
  setOpen: (id: string | null, focus?: PanelFocus) => void
  /** The module the current screen sits in (its marker bar). */
  currentRootId: string | null
  panelIdFor: (id: string) => string
  height: number
  emptyText: string
}

export function ModulesBar({
  model, barRef, openId, setOpen, currentRootId, panelIdFor, height, emptyText,
}: ModulesBarProps) {
  const nodes = model.roots
  const [focusedId, setFocusedId] = useState<string | null>(null)
  // The one module in the tab order: the open one, else the one last focused,
  // else the current screen's module, else the first.
  const tabbable = (openId && nodes.some((n) => n.item.id === openId) ? openId : null)
    || (focusedId && nodes.some((n) => n.item.id === focusedId) ? focusedId : null)
    || (currentRootId && nodes.some((n) => n.item.id === currentRootId) ? currentRootId : null)
    || nodes[0]?.item.id

  const buttons = () => [...(barRef.current?.querySelectorAll<HTMLElement>(':scope > [role="menuitem"]') || [])]
  const focusAt = (i: number) => {
    const els = buttons()
    if (!els.length) return
    const el = els[(i + els.length) % els.length]
    el.focus()
    const id = el.dataset.nodeId || null
    // A panel already open travels with the focus (menubar pattern); a screen
    // module has no panel, so moving onto it closes the one that was open.
    if (openId) {
      const node = nodes.find((n) => n.item.id === id)
      setOpen(node && hasPanel(node) ? id : null)
    }
  }

  if (!nodes.length) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0,
        background: 'var(--tw-mega-band)', color: 'var(--tw-mega-band-fg)', fontSize: 14,
      }}>
        {emptyText}
      </div>
    )
  }

  return (
    <div
      ref={barRef as RefObject<HTMLDivElement>}
      role="menubar"
      aria-label="Modules"
      className="tw-mega-bar"
      {...tw('action', 'shell-mega-modules')}
      style={{
        display: 'flex', alignItems: 'stretch', gap: 2, height, padding: '0 12px', flexShrink: 0,
        overflowX: 'auto', overflowY: 'hidden', background: 'var(--tw-mega-band)',
      }}
    >
      {nodes.map((n, idx) => {
        const panel = hasPanel(n)
        const open = openId === n.item.id
        const isCurrentModule = currentRootId === n.item.id
        const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
          switch (e.key) {
            case 'ArrowRight': e.preventDefault(); focusAt(idx + 1); break
            case 'ArrowLeft': e.preventDefault(); focusAt(idx - 1); break
            case 'Home': e.preventDefault(); focusAt(0); break
            case 'End': e.preventDefault(); focusAt(nodes.length - 1); break
            case 'ArrowDown':
            case 'Enter':
            case ' ':
              if (panel) { e.preventDefault(); setOpen(n.item.id, 'first') }
              break
            case 'ArrowUp':
              if (panel) { e.preventDefault(); setOpen(n.item.id, 'last') }
              break
            case 'Escape':
              if (open) { e.preventDefault(); e.stopPropagation(); setOpen(null) }
              break
            default:
          }
        }
        return (
          <button
            key={n.item.id}
            type="button"
            role="menuitem"
            data-node-id={n.item.id}
            tabIndex={n.item.id === tabbable ? 0 : -1}
            aria-haspopup={panel ? 'menu' : undefined}
            aria-expanded={panel ? open : undefined}
            aria-controls={panel && open ? panelIdFor(n.item.id) : undefined}
            aria-current={!panel && model.current?.id === n.item.id ? 'page' : undefined}
            className="tw-mega-mod"
            {...tw('nav', n.item.obj_name || n.item.title, {
              type: n.item.obj_type, folder: n.isFolder, selected: isCurrentModule, expanded: open,
            })}
            onFocus={() => setFocusedId(n.item.id)}
            onClick={() => {
              if (panel) { setOpen(open ? null : n.item.id); return }
              setOpen(null)
              model.open(n.item)
            }}
            // While a panel is open, pointing at another module switches to it
            // — the menubar's "menus follow the pointer" behaviour.
            onMouseEnter={() => { if (openId && panel && !open) setOpen(n.item.id) }}
            onKeyDown={onKeyDown}
            style={{
              position: 'relative', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
              padding: '0 16px', border: 'none', borderRadius: 0, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 14.5, fontWeight: 500, whiteSpace: 'nowrap',
            }}
          >
            <NavIcon node={n.item} size={17} />
            {n.item.title}
            {panel && <ChevronDown size={14} strokeWidth={2.2} aria-hidden />}
            {/* The module the open screen lives in — hidden while that module's
                own panel is open (the panel names it then). */}
            <span aria-hidden style={{
              position: 'absolute', left: 12, right: 12, bottom: 0, height: 3, borderRadius: '3px 3px 0 0',
              background: isCurrentModule && !open ? 'var(--tw-mega-band-bar)' : 'transparent',
            }} />
          </button>
        )
      })}
    </div>
  )
}
